import {
  BALANCE,
  DAY_TICKS,
  SHADOW_TICKS,
  buildCheck,
  buildableCells,
  orbitPhase,
  type Cell,
  type ModuleState,
  type StationState,
} from "@station-keeping/sim";
import * as Phaser from "phaser";
import { onStationEvents } from "../game/feed";
import { getEngine } from "../game/runtime";
import { useGameUi } from "../game/store";
import { Camera } from "./camera";
import { depthOf, groundCell, pickCell, toIso, type Block } from "./iso";
import { mix } from "./pixel";
import { MODULE_ANCHOR, MODULE_H, MODULE_W } from "./sprites";
import { PLANET_HEIGHT, TEX, moduleSprite, registerArt, registerPlanet, registerZoneEdge, type ArtBook } from "./textures";
import { HighwayView, ModuleView, PixelLabel, ShipView } from "./views";

type Image = Phaser.GameObjects.Image;
type Container = Phaser.GameObjects.Container;

const LABEL_MS = 1600;
/** Teinte de la nuit (multipliée) : les modules bleuissent et s'assombrissent, leurs fenêtres s'allument. */
const NIGHT_TINT = 0x7d88bd;
/** Limites des zones et marques, toujours sous les blocs. */
const EDGE_DEPTH = -1e9;
const MARK_DEPTH = -5e8;
/** Au plus deux pixels d'écran par pixel CSS : au-delà, le gain de netteté ne vaut pas le coût. */
const MAX_PIXEL_RATIO = 2;

/** Intensité de la nuit (0 à 1), avec un fondu de quelques secondes aux passages jour/ombre. */
function nightFactor(tick: number, alpha: number): number {
  const phase = orbitPhase(tick) + alpha;
  if (phase < DAY_TICKS) return 0;
  const into = phase - DAY_TICKS;
  return Math.min(1, into / 6, (SHADOW_TICKS - into) / 6);
}

/** Ce qu'un geste a touché sur le plan : un vaisseau, sinon une case. */
export type PickTarget = { kind: "ship"; id: string } | { kind: "cell"; cell: Cell };

/**
 * La scène Phaser : ciel du jour et de la nuit, soleil et planète (écran) ; limites des zones, modules,
 * vaisseaux (monde, trié par profondeur) ; marques, panneaux et gains (calque supérieur).
 */
class StationView extends Phaser.Scene {
  private book!: ArtBook;
  private skyNight!: Image;
  private skyDay!: Image;
  private stars!: Phaser.GameObjects.TileSprite;
  private sun!: Image;
  private planetNight!: Image;
  private planetDay!: Image;
  private world!: Container;
  private overlay!: Container;
  /** Limites : de la zone constructible (les anneaux ouverts), et de la gravité. */
  private openEdge!: Image;
  private gravityEdge!: Image;
  private edgeRings = -1;
  private readonly buildMarks: Image[] = [];
  private ghost!: Image;
  private blocked!: Image;
  private cursorMark!: Image;
  private selectMark!: Image;
  private highway!: HighwayView;
  private readonly moduleViews = new Map<string, ModuleView>();
  private readonly shipViews = new Map<string, ShipView>();
  private labels: PixelLabel[] = [];
  private unsubscribe: (() => void) | null = null;
  private fitted = false;
  private highlightKey = "";
  private buildableKey = "";
  private buildable: Cell[] = [];
  private night = -1;
  private nightTint = 0xffffff;
  private horizon = 0;
  /** Largeur (pixels d'art) des textures de la planète : elle ne fait que grandir avec l'écran. */
  private planetWidth = 0;

  constructor(private readonly owner: StationScene) {
    super({ key: "station" });
  }

  create(): void {
    this.book = registerArt(this.textures);
    this.game.canvas.setAttribute("aria-hidden", "true");

    this.skyNight = this.add.image(0, 0, TEX.sky("night")).setOrigin(0, 0).setDepth(0);
    this.skyDay = this.add.image(0, 0, TEX.sky("day")).setOrigin(0, 0).setDepth(0.1);
    this.stars = this.add.tileSprite(0, 0, 16, 16, TEX.stars).setOrigin(0, 0).setDepth(0.2);
    this.sun = this.add.image(0, 0, TEX.sun).setDepth(0.3).setVisible(false);
    this.planetNight = this.add.image(0, 0, TEX.pixel).setOrigin(0.5, 1).setDepth(0.4);
    this.planetDay = this.add.image(0, 0, TEX.pixel).setOrigin(0.5, 1).setDepth(0.5);
    this.world = this.add.container(0, 0).setDepth(2);
    this.overlay = this.add.container(0, 0).setDepth(3);

    // Le losange d'une zone est centré sur le noyau, à l'origine du monde.
    this.gravityEdge = this.add.image(0, 0, registerZoneEdge(this.textures, "gravity", BALANCE.grid.gravityFromRing - 1)).setDepth(EDGE_DEPTH);
    this.openEdge = this.add.image(0, 0, TEX.pixel).setDepth(EDGE_DEPTH + 1);
    this.world.add([this.gravityEdge, this.openEdge]);

    this.ghost = this.add.image(0, 0, TEX.module("dock")).setOrigin(MODULE_ANCHOR.x / MODULE_W, MODULE_ANCHOR.y / MODULE_H).setVisible(false);
    this.blocked = this.add.image(0, 0, TEX.highlight("blocked")).setVisible(false);
    this.world.add([this.ghost, this.blocked]);
    this.highway = new HighwayView(this, this.world, this.book);
    this.cursorMark = this.add.image(0, 0, TEX.highlight("cursor")).setVisible(false).setDepth(2e9);
    this.selectMark = this.add.image(0, 0, TEX.highlight("select")).setVisible(false).setDepth(2e9);
    this.overlay.add([this.cursorMark, this.selectMark]);

    this.scale.on(Phaser.Scale.Events.RESIZE, () => this.onResize());
    this.onResize();

    this.unsubscribe = onStationEvents((events) => {
      const state = getEngine()?.state;
      if (!state) return;
      for (const payment of events.payments) {
        const berth = state.modules.find((module) => module.id === payment.berth);
        if (!berth) continue;
        const center = toIso(berth.x, berth.y);
        this.labels.push(new PixelLabel(this, this.overlay, `+${payment.amount} cr`, center.x, center.y - 48, this.time.now));
      }
    });
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.unsubscribe?.());
  }

  private onResize(): void {
    const { width, height } = this.scale.gameSize;
    const ratio = this.owner.pixelRatio;
    const camera = this.owner.camera;
    camera.resize(width, height);
    // Sur téléphone, la station prend toute la largeur : moins de marge, pointes du losange rognées.
    const narrow = width / ratio < 600;
    camera.margin = narrow ? 0.2 : 1.5;
    camera.cropX = narrow ? 0.82 : 1;
    if (this.fitted && camera.follow) camera.fit();

    this.skyNight.setDisplaySize(width, height);
    this.skyDay.setDisplaySize(width, height);
    this.stars.setSize(width, height).setTileScale(ratio, ratio);

    // La planète, un pixel d'art par pixel CSS (comme les sprites à leur taille de base), aussi large que
    // l'écran : elle se redessine seulement quand l'écran s'élargit.
    const needed = Math.ceil(width / ratio / 128) * 128;
    if (needed > this.planetWidth) {
      this.planetWidth = needed;
      this.planetNight.setTexture(registerPlanet(this.textures, "night", needed));
      this.planetDay.setTexture(registerPlanet(this.textures, "day", needed));
    }
    const visible = Math.min(PLANET_HEIGHT * ratio, Math.round(height * 0.2));
    const bottom = height + (PLANET_HEIGHT * ratio - visible);
    this.planetNight.setScale(ratio).setPosition(Math.round(width / 2), bottom);
    this.planetDay.setScale(ratio).setPosition(Math.round(width / 2), bottom);
    this.horizon = height - visible;
    this.sun.setScale(ratio);
  }

  /** Ce qu'un point de l'écran (pixels du canevas) touche : un vaisseau, un module au pixel près, une case. */
  pick(sx: number, sy: number, groundOnly: boolean): PickTarget {
    const point = this.owner.camera.screenToWorld(sx, sy);
    if (!groundOnly) {
      let best: { id: string; depth: number } | null = null;
      for (const [id, view] of this.shipViews) {
        if (view.contains(point) && (!best || view.depth > best.depth)) best = { id, depth: view.depth };
      }
      if (best) return { kind: "ship", id: best.id };
    }
    if (groundOnly) return { kind: "cell", cell: groundCell(point.x, point.y) };
    const state = getEngine()?.state;
    const blocks: Block[] = (state?.modules ?? []).map((module) => {
      const info = moduleSprite(this.book, module.type, module.level);
      return {
        cell: { x: module.x, y: module.y },
        height: info.height,
        contains: (local) => info.mask.alphaAt(Math.floor(local.x + MODULE_ANCHOR.x), Math.floor(local.y + MODULE_ANCHOR.y)) >= 128,
      };
    });
    return { kind: "cell", cell: pickCell(point, blocks) };
  }

  override update(time: number, delta: number): void {
    const engine = getEngine();
    if (!engine || !this.world) return;
    const camera = this.owner.camera;
    const state = engine.state;
    const alpha = engine.alpha();
    const ui = useGameUi.getState();

    camera.rings = state.openRings;
    if (!this.fitted) {
      camera.fit();
      this.fitted = true;
    }
    camera.ease(1 - Math.exp(-Math.min(100, delta) / 120));
    this.world.setPosition(camera.x, camera.y).setScale(camera.scale);
    this.overlay.setPosition(camera.x, camera.y).setScale(camera.scale);
    this.stars.tilePositionX = -camera.x * 0.01;
    this.stars.tilePositionY = -camera.y * 0.01;

    const night = Math.round(nightFactor(state.tick, alpha) * 50) / 50;
    if (night !== this.night) {
      this.night = night;
      this.nightTint = mix(0xffffff, NIGHT_TINT, night);
      this.skyDay.setAlpha(1 - night);
      this.planetDay.setAlpha(1 - night);
      this.stars.setAlpha(0.3 + 0.7 * night);
    }
    this.updateSun(state, alpha);

    this.syncEdges(state);
    this.syncHighlights(state, engine.version, ui, time);
    this.highway.update(state.openRings, state.reputation, time, this.nightTint, this.night);
    this.syncModules(state, alpha, time, Boolean(ui.buildType));
    this.syncShips(state, alpha, time, delta, ui.selectedShip);
    this.labels = this.labels.filter((label) => label.update(time, LABEL_MS));
    this.world.sort("depth");
    this.overlay.sort("depth");
  }

  /** Le soleil traverse le ciel pendant le jour, puis passe derrière la planète : la station entre dans l'ombre. */
  private updateSun(state: StationState, alpha: number): void {
    const phase = orbitPhase(state.tick) + alpha;
    if (phase >= DAY_TICKS) {
      this.sun.setVisible(false);
      return;
    }
    const { width, height } = this.scale.gameSize;
    const u = phase / DAY_TICKS;
    const peak = height * 0.14;
    const x = width * (0.12 + 0.76 * u);
    const y = this.horizon - Math.sin(Math.PI * u) * (this.horizon - peak) + 24 * this.owner.pixelRatio;
    this.sun.setPosition(Math.round(x), Math.round(y)).setVisible(true);
  }

  /** Pas de sol : seulement le bord des anneaux ouverts, et celui où la gravité commence. */
  private syncEdges(state: StationState): void {
    if (state.openRings === this.edgeRings) return;
    this.edgeRings = state.openRings;
    this.openEdge.setTexture(registerZoneEdge(this.textures, "open", state.openRings));
    this.gravityEdge.setVisible(BALANCE.grid.gravityFromRing <= state.openRings);
  }

  private syncHighlights(state: StationState, version: number, ui: ReturnType<typeof useGameUi.getState>, time: number): void {
    // Les marques de sélection respirent un peu.
    this.selectMark.setAlpha(0.75 + 0.25 * Math.sin(time / 300));
    const key = [
      version,
      ui.buildType,
      ui.hover ? `${ui.hover.x},${ui.hover.y}` : "",
      ui.canvasFocused ? `${ui.cursor.x},${ui.cursor.y}` : "",
      ui.selection,
    ].join("|");
    if (key === this.highlightKey) return;
    this.highlightKey = key;

    // Cases constructibles du module en cours de placement.
    if (ui.buildType) {
      const cacheKey = `${version}|${ui.buildType}`;
      if (cacheKey !== this.buildableKey) {
        this.buildableKey = cacheKey;
        this.buildable = buildableCells(state, ui.buildType);
      }
    } else {
      this.buildable = [];
      this.buildableKey = "";
    }
    while (this.buildMarks.length < this.buildable.length) {
      const mark = this.add.image(0, 0, TEX.highlight("build"));
      this.world.add(mark);
      this.buildMarks.push(mark);
    }
    this.buildMarks.forEach((mark, index) => {
      const cell = this.buildable[index];
      if (!cell) {
        mark.setVisible(false);
        return;
      }
      const center = toIso(cell.x, cell.y);
      mark.setPosition(center.x, center.y).setDepth(MARK_DEPTH + depthOf(cell.x, cell.y)).setVisible(true);
    });

    // Aperçu du module sous le curseur, ou la case refusée.
    this.ghost.setVisible(false);
    this.blocked.setVisible(false);
    const target = ui.buildType ? (ui.hover ?? (ui.canvasFocused ? ui.cursor : null)) : null;
    if (ui.buildType && target) {
      const center = toIso(target.x, target.y);
      const legal = buildCheck(state, ui.buildType, target.x, target.y);
      if (legal === null || legal === "NOT_ENOUGH_CREDITS") {
        this.ghost
          .setTexture(TEX.module(ui.buildType))
          .setPosition(center.x, center.y)
          .setDepth(depthOf(target.x, target.y) + 0.5)
          .setAlpha(legal === null ? 0.85 : 0.35)
          .setVisible(true);
      } else {
        this.blocked.setPosition(center.x, center.y).setDepth(MARK_DEPTH + depthOf(target.x, target.y) + 1).setVisible(true);
      }
    }

    const selected = ui.selection ? state.modules.find((module) => module.id === ui.selection) : undefined;
    if (selected) {
      const center = toIso(selected.x, selected.y);
      this.selectMark.setPosition(center.x, center.y).setVisible(true);
    } else {
      this.selectMark.setVisible(false);
    }
    if (ui.canvasFocused) {
      const center = toIso(ui.cursor.x, ui.cursor.y);
      this.cursorMark.setPosition(center.x, center.y).setVisible(true);
    } else {
      this.cursorMark.setVisible(false);
    }
  }

  private syncModules(state: StationState, alpha: number, time: number, building: boolean): void {
    const seen = new Set<string>();
    for (const module of state.modules) {
      seen.add(module.id);
      let view = this.moduleViews.get(module.id);
      if (!view) {
        view = new ModuleView(this, this.world, this.overlay, module, this.book);
        this.moduleViews.set(module.id, view);
      }
      view.update(module, state, alpha, time, this.nightTint, this.night, building);
    }
    for (const [id, view] of this.moduleViews) {
      if (!seen.has(id)) {
        view.destroy();
        this.moduleViews.delete(id);
      }
    }
  }

  private syncShips(state: StationState, alpha: number, time: number, delta: number, selected: string | null): void {
    const berths = new Map<string, ModuleState>(state.modules.map((module) => [module.id, module]));
    const seen = new Set<string>();
    for (const ship of state.ships) {
      seen.add(ship.id);
      let view = this.shipViews.get(ship.id);
      if (!view) {
        view = new ShipView(this, this.world, ship, berths, this.book, state.openRings);
        this.shipViews.set(ship.id, view);
      }
      view.update(
        ship,
        berths,
        alpha,
        state.tick,
        time,
        Math.min(100, delta),
        state.openRings,
        this.nightTint,
        this.night,
        ship.id === selected,
      );
    }
    for (const [id, view] of this.shipViews) {
      if (!seen.has(id)) {
        view.destroy();
        this.shipViews.delete(id);
      }
    }
  }
}

/**
 * Le plan de la station rendu par Phaser, en vue isométrique et en pixel art. Le canevas est dessiné à la
 * densité réelle de l'écran (pixels nets) ; le composant React lui passe des coordonnées en pixels CSS.
 */
function screenRatio(): number {
  return Math.min(MAX_PIXEL_RATIO, Math.max(1, window.devicePixelRatio || 1));
}

export class StationScene {
  readonly camera = new Camera();
  /** Pixels du canevas par pixel CSS. */
  pixelRatio = 1;
  private game: Phaser.Game | null = null;
  private view: StationView | null = null;
  private host: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private ratioQuery: MediaQueryList | null = null;
  private cssInsets: Camera["insets"] = { top: 0, right: 0, bottom: 0, left: 0 };
  private readonly onRatioChange = () => this.applySize();

  mount(host: HTMLElement): void {
    const ratio = screenRatio();
    this.pixelRatio = ratio;
    this.host = host;
    const size = host.getBoundingClientRect();
    const view = new StationView(this);
    this.view = view;
    this.game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: host,
      transparent: true,
      banner: false,
      autoFocus: false,
      disableContextMenu: true,
      // Les gestes et le clavier passent par le composant React (l'entrée de Phaser est coupée).
      input: { keyboard: false, mouse: false, touch: false, gamepad: false },
      audio: { noAudio: true },
      render: { smoothPixelArt: true, transparent: true },
      scale: {
        mode: Phaser.Scale.NONE,
        parent: host,
        width: Math.max(1, Math.round(size.width * ratio)),
        height: Math.max(1, Math.round(size.height * ratio)),
        zoom: 1 / ratio,
        expandParent: false,
      },
      scene: view,
    });
    this.resizeObserver = new ResizeObserver(() => this.applySize());
    this.resizeObserver.observe(host);
    this.watchRatio();
  }

  destroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.ratioQuery?.removeEventListener("change", this.onRatioChange);
    this.ratioQuery = null;
    this.game?.destroy(true);
    this.game = null;
    this.view = null;
    this.host = null;
  }

  /** La densité de l'écran change (fenêtre passée sur un autre écran, zoom) : on le suit. */
  private watchRatio(): void {
    this.ratioQuery?.removeEventListener("change", this.onRatioChange);
    this.ratioQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
    this.ratioQuery.addEventListener("change", this.onRatioChange);
  }

  /** Taille du canevas : celle de son hôte, à la densité de l'écran. */
  private applySize(): void {
    const host = this.host;
    const game = this.game;
    if (!host || !game) return;
    const ratio = screenRatio();
    if (ratio !== this.pixelRatio) {
      this.pixelRatio = ratio;
      game.scale.setZoom(1 / ratio);
      this.setInsets(this.cssInsets);
      this.watchRatio();
    }
    const box = host.getBoundingClientRect();
    game.scale.resize(Math.max(1, Math.round(box.width * ratio)), Math.max(1, Math.round(box.height * ratio)));
  }

  /** Marges de l'interface (pixels CSS) : la vue cadre la station dans ce qui reste, en douceur. */
  setInsets(insets: Camera["insets"]): void {
    this.cssInsets = insets;
    const r = this.pixelRatio;
    this.camera.setInsets({ top: insets.top * r, right: insets.right * r, bottom: insets.bottom * r, left: insets.left * r });
  }

  recenter(): void {
    this.camera.fit();
  }

  /** Glisser (pixels CSS). */
  pan(dx: number, dy: number): void {
    this.camera.pan(dx * this.pixelRatio, dy * this.pixelRatio);
  }

  /** Zoom autour d'un point (pixels CSS) ; sans point, autour du centre. */
  zoomAt(factor: number, x?: number, y?: number): void {
    const cx = x === undefined ? this.camera.width / 2 : x * this.pixelRatio;
    const cy = y === undefined ? this.camera.height / 2 : y * this.pixelRatio;
    this.camera.zoomAt(cx, cy, factor);
  }

  /**
   * Ce qu'un point de l'écran (pixels CSS) touche, `null` tant que la scène démarre. En construction, on vise
   * toujours la case du sol, même derrière un bâtiment.
   */
  pick(x: number, y: number, groundOnly = false): PickTarget | null {
    return this.view?.sys.isActive() ? this.view.pick(x * this.pixelRatio, y * this.pixelRatio, groundOnly) : null;
  }

  /** Garde visible une case atteinte au clavier. */
  ensureCellVisible(cell: Cell): void {
    this.camera.ensureVisible(toIso(cell.x, cell.y), 56);
  }
}
